/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * Single module card with click-to-expand behaviour. Collapsed shows
 * a 120-char preview; expanded shows the full content (whitespace-
 * preserved) and, if present, links to related case studies.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getModuleIcon } from './ManhattanIcons';

const PREVIEW_CHARS = 120;

const KnowledgeModuleCard = ({ module: m }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = getModuleIcon(m.module_key);

  const content = m.content || '';
  const preview =
    content.length > PREVIEW_CHARS
      ? `${content.slice(0, PREVIEW_CHARS).trimEnd()}…`
      : content;

  const toggle = () => setExpanded((v) => !v);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={toggle}
      onKeyDown={onKeyDown}
      className="group flex flex-col text-left bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 cursor-pointer"
    >
      <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
      </div>

      <h3 className="font-bold text-base text-brand-text">{m.title}</h3>

      {m.subtitle && (
        <p className="mt-1 text-sm text-brand-muted2">{m.subtitle}</p>
      )}

      {!expanded ? (
        <p className="mt-3 text-sm text-brand-muted leading-relaxed">
          {preview}
        </p>
      ) : (
        <div className="mt-3 text-sm text-brand-text2 leading-relaxed whitespace-pre-line">
          {content}
        </div>
      )}

      {expanded &&
        Array.isArray(m.related_case_study_slugs) &&
        m.related_case_study_slugs.length > 0 && (
          <div className="mt-4 pt-4 border-t border-brand-border">
            <p className="text-[11px] font-bold tracking-wider uppercase text-brand-cyan mb-2">
              Related case studies
            </p>
            <div className="flex flex-wrap gap-1.5">
              {m.related_case_study_slugs.map((slug) => (
                <Link
                  key={slug}
                  to={`/domain/wms/case-studies/${slug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-block rounded-brandPill bg-brand-bgOff2 text-brand-cyan px-3 py-1 text-xs font-semibold hover:bg-brand-cyan/20 transition-colors"
                >
                  {slug}
                </Link>
              ))}
            </div>
          </div>
        )}

      <p className="mt-auto pt-4 text-sm font-semibold text-brand-cyan group-hover:translate-x-1 transition-transform">
        {expanded ? '↑ Collapse' : 'Learn more →'}
      </p>
    </div>
  );
};

export default KnowledgeModuleCard;
